/* NanoGrow Max: shared runtime. Loaded as a deferred ES module on every page.
   Keep this file small: features with heavier code live in their own n-*.js asset. */

const cfg = window.NGM_CONFIG || {};
const routes = cfg.routes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update', cart_url: '/cart', root: '/' };
const SECTIONS = ['n-cart-drawer', 'n-cart-bubble'];

const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
const qs = (sel, root = document) => root.querySelector(sel);
const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];

/* ---------------- Helpers ---------------- */
const NGM = (window.NGM = window.NGM || {});
NGM.config = cfg;
NGM.money = (cents) => {
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString(cfg.locale || 'en-US', { style: 'currency', currency: cfg.currency || 'USD' });
};
NGM.loadScript = (src) =>
  new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) return res();
    const s = document.createElement('script');
    s.type = 'module'; s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
NGM.onVisible = (el, cb, opts = { rootMargin: '200px' }) => {
  if (!('IntersectionObserver' in window)) return cb(el);
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { io.unobserve(e.target); cb(e.target); } });
  }, opts);
  io.observe(el);
};
NGM.track = (event, data = {}) => {
  try {
    window.Shopify?.analytics?.publish?.(`ngm_${event}`, data);
    window.klaviyo?.push?.(['track', event, data]);
    window.dataLayer?.push?.({ event: `ngm_${event}`, ...data });
  } catch (e) { /* analytics must never break the page */ }
};
NGM.store = {
  get(k) { try { return JSON.parse(localStorage.getItem(`ngm:${k}`)); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(`ngm:${k}`, JSON.stringify(v)); } catch (e) { /* private mode */ } },
};

/* ---------------- Cart API ---------------- */
async function post(url, body) {
  const res = await fetch(`${url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: SECTIONS.join(','), sections_url: window.location.pathname }),
  });
  const json = await res.json();
  if (!res.ok || json.status) throw new Error(json.description || json.message || 'Cart error');
  return json;
}

function renderSections(sections) {
  if (!sections) return;
  Object.entries(sections).forEach(([id, html]) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    qsa(`[data-section-target="${id}"]`).forEach((target) => {
      const fresh = doc.querySelector(`[data-section-target="${id}"]`);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  });
}

let queue = Promise.resolve();
const serial = (fn) => (queue = queue.then(fn, fn));

NGM.cart = {
  async get() { return (await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json(); },

  add(items) {
    return serial(async () => {
      try {
        const json = await post(routes.cart_add_url, { items: Array.isArray(items) ? items : [items] });
        renderSections(json.sections);
        const cart = await NGM.cart.get();
        emit('ngm:cart:updated', { cart, source: 'add', items: json.items });
        NGM.track('add_to_cart', { items: (json.items || []).map((i) => ({ id: i.variant_id, q: i.quantity, price: i.final_price })) });
        return cart;
      } catch (err) { emit('ngm:cart:error', { message: err.message }); throw err; }
    });
  },

  change(id, quantity) {
    return serial(async () => {
      try {
        const json = await post(routes.cart_change_url, { id: String(id), quantity });
        renderSections(json.sections);
        emit('ngm:cart:updated', { cart: json, source: 'change' });
        return json;
      } catch (err) { emit('ngm:cart:error', { message: err.message }); throw err; }
    });
  },

  update(payload) {
    return serial(async () => {
      try {
        const json = await post(routes.cart_update_url, payload);
        renderSections(json.sections);
        emit('ngm:cart:updated', { cart: json, source: 'update' });
        return json;
      } catch (err) { emit('ngm:cart:error', { message: err.message }); throw err; }
    });
  },

  /* Adds a discount code while keeping any codes already applied (cart/update replaces the list). */
  async applyDiscount(code) {
    if (!code) return null;
    const cart = await NGM.cart.get();
    const existing = (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
    const codes = [...new Set([...existing, code])];
    NGM.store.set('pending_discount', code);
    const updated = await NGM.cart.update({ discount: codes.join(',') });
    const ok = (updated.discount_codes || []).some((d) => d.code.toUpperCase() === code.toUpperCase() && d.applicable);
    if (ok) { NGM.store.set('pending_discount', null); NGM.store.set('applied_discount', code); }
    emit('ngm:discount:applied', { code, ok });
    return updated;
  },
};

/* Re-apply a won code that couldn't apply to an empty cart yet (codes need items to be "applicable"). */
document.addEventListener('ngm:cart:updated', async (e) => {
  const pending = NGM.store.get('pending_discount');
  const cart = e.detail?.cart;
  if (!pending || !cart?.item_count || e.detail.source === 'update') return;
  NGM.cart.applyDiscount(pending).catch(() => {});
});

/* ---------------- Free gift with the Grow Max Bundle ----------------
   A native automatic BXGY discount makes the gift variant free once qualifying
   grow products reach the threshold. The theme just keeps exactly one gift line in the cart. */
const gift = cfg.gift || {};
async function syncGift(cart) {
  if (!gift.enabled || !gift.variantId || !cart) return;
  // Legacy gift lines from the old theme / BOGOS app (shared cart across themes): the theme's own
  // gift line replaces them, so they are removed to avoid stacking multiple free bottles.
  const legacyPatterns = (gift.legacyHandles || []).filter(Boolean);
  const legacyVariants = (gift.legacyVariantIds || []).map(Number);
  const legacy = cart.items.filter((i) => legacyVariants.includes(i.variant_id) || legacyPatterns.some((p) => i.handle.includes(p)));
  const handles = (gift.handles || []).map((h) => h.trim());
  const qualifying = cart.items
    .filter((i) => handles.includes(i.handle) && !i.properties?._ngm_gift)
    .reduce((sum, i) => sum + i.original_line_price, 0);
  const giftLines = cart.items.filter((i) => i.properties?._ngm_gift);
  const shouldHave = qualifying >= gift.thresholdCents;
  const updates = {};
  legacy.forEach((l) => { updates[l.key] = 0; });
  if (!shouldHave) giftLines.forEach((l) => { updates[l.key] = 0; });
  else giftLines.forEach((l, idx) => { if (idx > 0) updates[l.key] = 0; else if (l.quantity !== 1) updates[l.key] = 1; });
  if (Object.keys(updates).length) await NGM.cart.update({ updates });
  if (shouldHave && giftLines.length === 0) {
    await NGM.cart.add({ id: gift.variantId, quantity: 1, properties: { _ngm_gift: 'Free with Grow Max Bundle' } });
  }
}
document.addEventListener('ngm:cart:updated', (e) => {
  if (e.detail?.giftSync) return;
  syncGift(e.detail?.cart).catch(() => {});
});

/* ---------------- Cart drawer ---------------- */
class NCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
    this.dialog?.addEventListener('close', () => document.documentElement.classList.remove('n-lock'));
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-cart-open]');
      if (t) { e.preventDefault(); this.open(); }
    });
    document.addEventListener('ngm:cart:updated', (e) => { if (e.detail?.source === 'add') this.open(); });
  }
  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.classList.add('n-lock');
    NGM.track('cart_open');
  }
  close() { this.dialog?.close(); }
  async onClick(e) {
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); return; }
    const qtyBtn = e.target.closest('[data-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const line = qtyBtn.closest('[data-line-key]');
      const current = Number(line.querySelector('input[name="qty"]')?.value || 1);
      const next = qtyBtn.dataset.qty === 'plus' ? current + 1 : qtyBtn.dataset.qty === 'minus' ? current - 1 : 0;
      line.classList.add('is-busy');
      await NGM.cart.change(line.dataset.lineKey, Math.max(0, next)).catch(() => line.classList.remove('is-busy'));
      return;
    }
    const upsell = e.target.closest('[data-upsell-add]');
    if (upsell) {
      e.preventDefault();
      upsell.classList.add('is-loading');
      const items = [{ id: Number(upsell.dataset.upsellAdd), quantity: 1 }];
      try {
        if (upsell.dataset.replaceKey) await NGM.cart.change(upsell.dataset.replaceKey, 0);
        await NGM.cart.add(items);
        NGM.track('upsell_add', { variant: upsell.dataset.upsellAdd, slot: upsell.dataset.slot || 'drawer' });
      } finally { upsell.classList.remove('is-loading'); }
    }
  }
  async onChange(e) {
    const input = e.target.closest('input[name="qty"]');
    if (input) {
      const line = input.closest('[data-line-key]');
      await NGM.cart.change(line.dataset.lineKey, Math.max(0, Number(input.value) || 0)).catch(() => {});
    }
  }
}
customElements.define('n-cart-drawer', NCartDrawer);

/* Discount form inside the drawer */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-discount-form]');
  if (!form) return;
  e.preventDefault();
  const input = form.querySelector('input[name="discount"]');
  const msg = form.querySelector('[data-discount-msg]');
  const code = input.value.trim();
  if (!code) return;
  form.classList.add('is-busy');
  try {
    const cart = await NGM.cart.applyDiscount(code);
    const hit = (cart?.discount_codes || []).find((d) => d.code.toUpperCase() === code.toUpperCase());
    if (msg) msg.textContent = hit?.applicable ? `${code.toUpperCase()} applied` : `${code.toUpperCase()} can't be applied to this cart`;
  } catch (err) { if (msg) msg.textContent = err.message; }
  form.classList.remove('is-busy');
});

/* ---------------- Ajax product forms ---------------- */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-ngm-add]');
  if (!form) return;
  e.preventDefault();
  const btn = form.querySelector('[type="submit"]');
  const errorEl = form.querySelector('[data-form-error]');
  btn?.classList.add('is-loading');
  btn?.setAttribute('aria-disabled', 'true');
  if (errorEl) errorEl.hidden = true;
  const fd = new FormData(form);
  const items = [];
  // Supports multi-item forms: inputs named items[0][id], items[0][quantity] ...
  const multi = [...fd.keys()].some((k) => k.startsWith('items['));
  if (multi) {
    const map = {};
    for (const [k, v] of fd.entries()) {
      const m = k.match(/^items\[(\d+)\]\[(\w+)\]$/);
      if (m) (map[m[1]] ||= {})[m[2]] = v;
    }
    Object.values(map).forEach((i) => i.id && items.push({ id: Number(i.id), quantity: Number(i.quantity || 1) }));
  } else {
    const props = {};
    for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
    items.push({ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1), properties: props });
  }
  try {
    await NGM.cart.add(items);
  } catch (err) {
    if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
  } finally {
    btn?.classList.remove('is-loading');
    btn?.removeAttribute('aria-disabled');
  }
});

/* ---------------- Count-up numbers ---------------- */
function countUp(el) {
  const target = parseFloat(el.dataset.countup);
  if (Number.isNaN(target)) return;
  const decimals = Number(el.dataset.decimals || 0);
  const prefix = el.dataset.prefix || '';
  const suffix = el.dataset.suffix || '';
  const fmt = (v) => `${prefix}${v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = fmt(target); return; }
  const from = parseFloat(el.dataset.from || 0);
  const dur = Number(el.dataset.duration || 1600);
  const start = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - start) / dur);
    const eased = 1 - Math.pow(1 - p, 3);
    el.textContent = fmt(from + (target - from) * eased);
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/* ---------------- Reveal on scroll ---------------- */
function initMotion(root = document) {
  const reveal = qsa('[data-reveal]:not(.is-in)', root);
  const counters = qsa('[data-countup]:not([data-counted])', root);
  if (!('IntersectionObserver' in window)) {
    reveal.forEach((el) => el.classList.add('is-in'));
    counters.forEach(countUp);
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      if (el.hasAttribute('data-countup')) { el.setAttribute('data-counted', ''); countUp(el); }
      else el.classList.add('is-in');
      io.unobserve(el);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.15 });
  reveal.forEach((el) => io.observe(el));
  counters.forEach((el) => io.observe(el));
}
initMotion();
document.addEventListener('shopify:section:load', (e) => initMotion(e.target));

/* ---------------- Header: sticky state & mobile menu ---------------- */
const header = qs('[data-header]');
if (header) {
  let last = 0;
  const onScroll = () => {
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > 8);
    header.classList.toggle('is-hidden', y > 400 && y > last && !document.documentElement.classList.contains('n-lock'));
    last = y;
  };
  addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}
document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-menu-toggle]');
  if (!t) return;
  const menu = document.getElementById(t.getAttribute('aria-controls'));
  if (!menu) return;
  if (menu.open) menu.close(); else { menu.showModal(); }
});

/* ---------------- Sticky add-to-cart bar ---------------- */
const sticky = qs('[data-sticky-atc]');
const anchor = qs('[data-sticky-anchor]');
if (sticky && anchor && 'IntersectionObserver' in window) {
  new IntersectionObserver(([e]) => {
    const show = !e.isIntersecting && e.boundingClientRect.top < 0;
    sticky.classList.toggle('is-visible', show);
    sticky.setAttribute('aria-hidden', String(!show));
  }).observe(anchor);
}

/* ---------------- Persist UTM + apply ?discount= links ---------------- */
const params = new URLSearchParams(location.search);
if (params.get('utm_source')) NGM.store.set('utm', Object.fromEntries([...params].filter(([k]) => k.startsWith('utm_'))));

/* Initial gift check (e.g. returning visitor with a qualifying cart) */
if (cfg.cartCount > 0) NGM.cart.get().then(syncGift).catch(() => {});

document.documentElement.classList.remove('no-js');
emit('ngm:ready', {});
